import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getCurrentCountry from "@MasterBluePrint/Handlers/Common/GetCurrentCountry";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    text = "text",
    enableGeoIpBasedGuest = "enableGeoIpBasedGuest",
    daysToKeepCookie = "daysToKeepCookie"
}

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const accountSettings = getSettingsCollection(state).accountSettings;
    const returnUrl = getReturnUrl(state);
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    return {
        allowCreateAccount: accountSettings.allowCreateAccount,
        allowGuestCheckout: accountSettings.allowGuestCheckout && !getCurrentUserIsGuest(state) && referredFromShipping,
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
    getCurrentCountry
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.text]: string;
        [fields.enableGeoIpBasedGuest]: boolean;
        [fields.daysToKeepCookie]: number;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInCreateNewAccountStyles {
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTitleGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItem?: GridItemProps;
    signInCreateNewAccountButtonGridItem?: GridItemProps;
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
}

export const signInCreateNewAccountStyles: SignInCreateNewAccountStyles = {
    signInCreateNewAccountTitle: {
        variant: "h4",
    },
    signInCreateNewAccountTitleGridItem: {
        width: 12,
    },
    signInCreateNewAccountTextGridItem: {
        width: 12,
    },
    signInCreateNewAccountButtonGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                        null,
                        css`
                            flex-direction: row;
                            justify-content: space-between;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    checkoutAsGuestButton: {
        variant: "tertiary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            margin-bottom: 1rem;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    createNewAccountButton: { variant: "secondary" },
    centeringWrapper: {
        css: css`
            background: rgba(0, 37, 84, 0.2);
            height: 100%;
            width: 100%;
            position: fixed;
            z-index: 99;
            left: 0;
            top: 0;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
            color: #002554;
        `,
    },
};

/**
 * @deprecated Use signInCreateNewAccountStyles instead.
 */
export const signInCreateNewAccount = signInCreateNewAccountStyles;
const styles = signInCreateNewAccountStyles;

const SignInCreateNewAccount: FC<Props> = ({
    history,
    fields,
    allowCreateAccount,
    allowGuestCheckout,
    returnUrl,
    createAccountUrl,
    signInAsGuest,
    getCurrentCountry
}) => {
    const [showLoader, setShowLoader] = useState(false);
    const [isSignInPageLoader, setSignInPageLoader] = React.useState(true);

    if (!allowCreateAccount) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }
        setShowLoader(true);
        const loadingOverlaySpinner = document.querySelector('[data-test-selector="loadingOverlaySpinner"]');
        if (loadingOverlaySpinner) {
            loadingOverlaySpinner.classList.add("hidden-element");
          }
        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }
        history.push(createAccountUrl);
    };
    const [countryName, setCountry] = useState("US");

    useEffect(() => {
        if (fields.enableGeoIpBasedGuest) {
            getCurrentCountry({
                daysToKeepCookie: fields.daysToKeepCookie,
                onSuccess: (code: string) => {
                    setCountry(code);
                },
            });
        }

        if (document.querySelector('[data-test-selector="signIn"]') !== null && document.querySelector('[data-test-selector="signIn"]') !== undefined) {                
            setSignInPageLoader(false);
        } else {
            setSignInPageLoader(true);
        }
    }, []);

    return (
        <>
            {showLoader && <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>}
            {isSignInPageLoader && <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>}
            <GridContainer {...styles.signInCreateNewAccountGridContainer}>
                <GridItem {...styles.signInCreateNewAccountTitleGridItem}>
                    <Typography {...styles.signInCreateNewAccountTitle}>{translate("Create New Account")}</Typography>
                </GridItem>
                <GridItem {...styles.signInCreateNewAccountTextGridItem}>
                    <Typography {...styles.signInCreateNewAccountText}>{fields.text}</Typography>
                </GridItem>
                <GridItem {...styles.signInCreateNewAccountButtonGridItem}>
                    {allowGuestCheckout && countryName === "US" && (
                        <Button
                            {...styles.checkoutAsGuestButton}
                            onClick={handleCheckoutAsGuest}
                            data-test-selector="signInCreateNewAccount_checkoutAsGuest"
                        >
                            {translate("Checkout as Guest")}
                        </Button>
                    )}
                    {createAccountUrl && (
                        <Button
                            {...styles.createNewAccountButton}
                            onClick={handleCreateNewAccount}
                            data-test-selector="signInCreateNewAccount_createNewAccount"
                        >
                            {translate("Create Account")}
                        </Button>
                    )}
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
     component: connect(mapStateToProps, mapDispatchToProps)(withHistory(SignInCreateNewAccount)),
    definition: {
        allowedContexts: ["SignInPage"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.text,
                displayName: "Main Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Translatable",
            },
            {
                name: fields.enableGeoIpBasedGuest,
                displayName: "Enable GeoIp Based Guest Checkout?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
            },
            {
                name: fields.daysToKeepCookie,
                displayName: "Days to keep cookie",
                tooltip: "Enter the number of days to keep the country code cookie.",
                editorTemplate: "IntegerField",
                defaultValue: 30,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
